<template>
  <div class="gap-small pos-relative cols-1">
    <div class="pos-relative w-100 gap-thin flex-stretch flex-nowrap flex">
      <div class="bg-light h-100 o-hidden pos-relative radius-small aspect-1x1" style="flex: 1 0 auto">
        <img 
          v-if="images && images[0]"
          loading="lazy" 
          class="w-100 object-fit-cover pos-absolute h-100"    
          :src="(FILE_SERVER_URL || '') + images[0]"
        >
        <PlaceholderImage
          v-else 
          class="bg-light pos-absolute radius-small w-100 h-100"
        />
      </div>
      
      <div class="w-100 pos-relative">
        <p class="truncate fw-medium">{{ name }}</p>
        
        <div class="mn-t-thin">
          <p v-if="price != null && quantity != null">
            <span class="t-dark mn-r-micro d-inline-block fw-demi">
              {{ returnCurrency() }}{{ totalPrice }}
            </span>
            <span v-if="isRental && rentDates.start && rentDates.end" class="p-small opacity-50">
              {{ returnCurrency() }}{{ price }}
              <span v-if="quantity > 1">× {{ quantity }} {{ unit || 'pcs' }}</span>
              × {{ rentalDurationLabel }}
            </span>
            <span v-else class="p-small opacity-50">{{ returnCurrency() }}{{ price }} × {{ quantity || 0 }}  {{ unit || 'pcs' }}</span>
          </p>
          
          <p v-else-if="price != null && quantity == null" class="t-dark mn-r-micro d-inline-block fw-demi">
            {{ returnCurrency() }}{{ price }}
          </p>
          
          <p v-else-if="price == null && quantity != null" class="p-small opacity-50">
            {{ quantity }} {{ unit || 'pcs' }}
          </p>
        </div>
        
        <slot></slot>
        <div v-if="showControls" class="mn-t-thin gap-thin flex-justify-start flex">
          <div
            v-if="isRental && rentDates.start && rentDates.end"
            @click="editable ? openRentDatePopup() : null"
            :class="['flex gap-thin flex-v-center pd-thin w-100 bg-light radius-small', editable ? 'cursor-pointer' : '']"
          >
            <IconCalendar class="i-medium" />
            <p class="fw-medium opacity-50">
              {{ rentDateLabel }}
            </p>
          </div>

          <template v-if="editable && !isRental">
            <IconAdd
              @click="increase"
              class="hover:scale-[1.05] cursor-pointer bg-light i-big pd-thin radius-small"
            />
            <IconMinus
              @click="decrease"
              class="hover:scale-[1.05] cursor-pointer bg-light i-big pd-thin radius-small"
            />
            <IconDelete
              @click="remove"
              class="hover:scale-[1.05] cursor-pointer bg-light i-big pd-thin radius-small"
            />
          </template>

          <IconEdit
            v-if="isRental && editable"
            @click="openRentDatePopup"
            class="hover:scale-[1.05] cursor-pointer bg-light i-big pd-small radius-small"
          />
        </div>
      </div>
    </div>
    
  </div>
</template>

<script setup>
import { ref, computed, getCurrentInstance } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import IconAdd from '@martyrs/src/modules/icons/navigation/IconAdd.vue'
import IconEdit from '@martyrs/src/modules/icons/navigation/IconEdit.vue'
import IconMinus from '@martyrs/src/modules/icons/navigation/IconMinus.vue'
import IconDelete from '@martyrs/src/modules/icons/navigation/IconDelete.vue'
import IconCalendar from '@martyrs/src/modules/icons/entities/IconCalendar.vue'
import PlaceholderImage from '@martyrs/src/modules/icons/placeholders/PlaceholderImage.vue'
import { useGlobalMixins } from '@martyrs/src/modules/core/views/mixins/mixins.js'
import { calcRentTotalPrice, formatRentDuration, formatRentDateLabel } from '@martyrs/src/modules/orders/utils/rent-price.js'

const router = useRouter()
const { locale } = useI18n()
const { proxy } = getCurrentInstance()
const { returnCurrency, formatDate } = useGlobalMixins()

const emits = defineEmits(['increase', 'decrease', 'remove', 'updateRentDates'])
const props = defineProps({
  productId: String,
  variantId: String,
  images: Array,
  name: String,
  quantity: Number,
  unit: String,
  dates: Object,
  listing: String,
  rentPeriod: { type: String, default: 'day' },
  price: Number,
  editable: Boolean,
  increase: Function,
  decrease: Function,
  remove: Function,
})

const isRental = computed(() => props.listing === 'rent')
const showControls = computed(() => props.editable || isRental.value)

const rentDates = ref({
  start: props.dates?.start || null,
  end: props.dates?.end || null
})

const isShortPeriod = computed(() => ['hour', '4hours', 'flat'].includes(props.rentPeriod))

const rentalDurationLabel = computed(() => {
  if (!rentDates.value.start || !rentDates.value.end) return ''
  return formatRentDuration(rentDates.value.start, rentDates.value.end, props.rentPeriod)
})

const rentDateLabel = computed(() => {
  return formatRentDateLabel(rentDates.value.start, rentDates.value.end, props.rentPeriod, formatDate)
})

const totalPrice = computed(() => {
  if (isRental.value && rentDates.value.start && rentDates.value.end) {
    return calcRentTotalPrice(props.price, props.quantity, rentDates.value.start, rentDates.value.end, props.rentPeriod)
  }
  return props.quantity * props.price
})

const isRentDatePopupOpen = ref(false)

function increase() {
  props.increase?.()
  emits('increase')
}

function decrease() {
  props.decrease?.()
  emits('decrease')
}

function remove() {
  props.remove?.()
  emits('remove')
}

async function openRentDatePopup() {
  try {
    const selectedDates = await proxy.$dateSelector(
      props.productId,
      props.variantId,
      props.quantity,
      props.price,
      {
        showCancelButton: props.editable,
        onCancel: props.editable ? () => props.remove?.() : null,
        enableTime: isShortPeriod.value,
        periodType: props.rentPeriod,
        initialDates: rentDates.value.start ? { start: rentDates.value.start, end: rentDates.value.end } : null
      }
    );
    
    if (selectedDates) {
      updateRentDates(selectedDates)
    }
  } catch (error) {
    console.error('Error selecting dates:', error)
  }
}

function updateRentDates(dates) {
  rentDates.value = dates
  emits('updateRentDates', props.productId, props.variantId, dates)
}
</script>

<style lang="scss" scoped>
</style>